import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';
import '../styles/global.css';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || '';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [magicEmail, setMagicEmail] = useState('');
  const [mode, setMode] = useState('login'); // login | magic | sent
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle magic link token from URL
  const token = searchParams.get('token');
  if (token) {
    api.get(`/auth/magic?token=${token}`)
      .then(res => {
        login(res.data.client, res.data.token);
        if (res.data.isNewClient) {
          navigate('/welcome');
        } else {
          navigate('/');
        }
      })
      .catch(() => setError('This link has expired. Please request a new one.'));
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      login(res.data.client, res.data.token);
      navigate('/');
    } catch (err) {
      setError('Incorrect email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/magic-link', { email: magicEmail });
      setMode('sent');
    } catch (err) {
      setError('No account found with this email address.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: 'var(--teal)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      {/* Logo */}
      <div style={{ marginBottom: '28px' }}>
        {LOGO_URL ? (
          <img src={LOGO_URL} alt="Bump Baby Imaging" style={{ height: '72px', filter: 'brightness(0) invert(1)' }} />
        ) : (
          <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '26px', color: 'white', letterSpacing: '1px' }}>
            BUMP <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span> BABY IMAGING
          </div>
        )}
      </div>

      <div className="card" style={{ maxWidth: '420px', width: '100%' }}>
        {mode === 'login' && (
          <>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '6px' }}>
              Welcome <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span>
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginBottom: '28px', lineHeight: '1.5' }}>
              Log in to view your precious memories from Bump Baby Imaging.
            </p>
            {error && <p style={{ color: 'var(--coral-dark)', fontSize: '13px', marginBottom: '16px', background: 'var(--coral-light)', padding: '10px 14px', borderRadius: '8px' }}>{error}</p>}
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
              <button className="btn-primary" type="submit" disabled={loading}>{loading ? 'Logging in...' : 'Log In'}</button>
            </form>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0', color: 'var(--text-soft)', fontSize: '12px' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
              or
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            </div>
            <button className="btn-secondary" onClick={() => setMode('magic')}>Send me a magic link</button>
            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'var(--text-soft)' }}>
              <span style={{ color: 'var(--teal)', cursor: 'pointer', fontWeight: 700 }} onClick={() => navigate('/forgot-password')}>Forgot your password?</span>
            </p>
          </>
        )}

        {mode === 'magic' && (
          <>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '6px' }}>Magic link</h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginBottom: '28px', lineHeight: '1.5' }}>
              Enter your email and we'll send you a link to log in instantly — no password needed.
            </p>
            {error && <p style={{ color: 'var(--coral-dark)', fontSize: '13px', marginBottom: '16px', background: 'var(--coral-light)', padding: '10px 14px', borderRadius: '8px' }}>{error}</p>}
            <form onSubmit={handleMagicLink}>
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" placeholder="your@email.com" value={magicEmail} onChange={e => setMagicEmail(e.target.value)} required />
              </div>
              <button className="btn-primary" type="submit" disabled={loading}>{loading ? 'Sending...' : 'Send Magic Link'}</button>
            </form>
            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'var(--text-soft)' }}>
              <span style={{ color: 'var(--teal)', cursor: 'pointer', fontWeight: 700 }} onClick={() => setMode('login')}>← Back to login</span>
            </p>
          </>
        )}

        {mode === 'sent' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ width: '72px', height: '72px', background: 'var(--teal-light)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '3px solid var(--teal)' }}>
                <span style={{ fontSize: '28px' }}>✉</span>
              </div>
              <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '8px' }}>Check your email</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-soft)', lineHeight: '1.6' }}>
                We've sent a magic link to <strong style={{ color: 'var(--teal-dark)' }}>{magicEmail}</strong>. Click it to log in instantly.
              </p>
            </div>
            <div style={{ background: 'var(--teal-light)', borderRadius: '12px', padding: '14px 18px', fontSize: '13px', color: 'var(--teal-dark)' }}>
              The link expires in 30 minutes. If you don't see it, check your spam folder.
            </div>
            <button className="btn-secondary" style={{ marginTop: '20px' }} onClick={() => setMode('magic')}>Resend email</button>
          </>
        )}
      </div>

      <footer style={{ marginTop: '32px', fontSize: '12px', color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
        <p>Made with <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span> by Bump Baby Imaging</p>
        <p style={{ marginTop: '4px' }}>info@bumpbabyimaging.com.au</p>
      </footer>
    </div>
  );
}
